/**
 * AdminChatContentEditor — Editor for AI Chat help topics.
 *
 * Reads/writes globalSettings/chatContent in Firestore.
 * Subtab under AdminDataEditor (Data tab).
 *
 * Help Topics — 7 topics (export, share, record, upload, navigate, settings, overview)
 *
 * See: docs/features/admin/admin-chat-content.md
 */
import React, { useState, useEffect, useCallback } from "react";
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../utils/authContext";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { Save, RotateCcw } from "lucide-react";

const DOC_PATH = "globalSettings";
const DOC_ID = "chatContent";

// ── Help topic keys (must match chatFunctions.json topic enum) ──
const HELP_TOPIC_KEYS = ["export", "share", "record", "upload", "navigate", "settings", "overview"];

function formatKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// ── Main Component ──
const AdminChatContentEditor = () => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);

  // Working copies (editable)
  const [helpTopics, setHelpTopics] = useState({});

  // Saved copy (for revert / dirty detection)
  const [savedHelpTopics, setSavedHelpTopics] = useState({});

  // Selected key
  const [selectedTopic, setSelectedTopic] = useState(null);

  // Dirty flag
  const helpDirty = JSON.stringify(helpTopics) !== JSON.stringify(savedHelpTopics);

  // ── Load ──
  const fetchContent = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, DOC_PATH, DOC_ID));
      if (snap.exists()) {
        const data = snap.data();
        const ht = data.helpTopics || {};
        setHelpTopics(ht);
        setSavedHelpTopics(JSON.parse(JSON.stringify(ht)));
      } else {
        setError("No chat content found. Run the seed script: node scripts/seedChatContent.js");
      }
    } catch (err) {
      console.error("[AdminChatContentEditor] Error loading:", err);
      setError("Failed to load chat content.");
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // ── Field change handler ──
  const handleTopicChange = (key, field, value) => {
    setHelpTopics((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  // ── Revert handler ──
  const revertHelpTopics = () => {
    setHelpTopics(JSON.parse(JSON.stringify(savedHelpTopics)));
  };

  // ── Save handler ──
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(null);
    try {
      const updateData = {
        helpTopics,
        lastModifiedBy: currentUser?.uid || "unknown",
        lastModifiedAt: serverTimestamp(),
      };

      await setDoc(doc(db, DOC_PATH, DOC_ID), updateData, { merge: true });

      // Write audit log (userId must match auth.uid per firestore.rules)
      await addDoc(collection(db, "auditLogs"), {
        action: "update_chat_content",
        section: "helpTopics",
        userId: currentUser?.uid,
        performedByEmail: currentUser?.email || "unknown",
        timestamp: serverTimestamp(),
      });

      // Update saved baseline
      setSavedHelpTopics(JSON.parse(JSON.stringify(helpTopics)));
      setSaveSuccess("Help topics saved.");
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      console.error("[AdminChatContentEditor] Save error:", err);
      setError("Failed to save. Check permissions.");
    }
    setSaving(false);
  };

  // ── Render ──
  if (isLoading) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  return (
    <div>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>{saveSuccess}</Alert>
      )}
      <Box sx={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", mb: 2 }}>
        Edit the help messages shown to users by the AI assistant.
        Changes take effect within 5 minutes (backend cache TTL).
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <h4 style={{ margin: 0 }}>Help Topics</h4>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Revert changes" arrow>
              <span>
                <Button size="small" variant="outlined" startIcon={<RotateCcw size={14} />} onClick={revertHelpTopics} disabled={!helpDirty || saving}>
                  Revert
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Save help topics" arrow>
              <span>
                <Button size="small" variant="contained" startIcon={<Save size={14} />} onClick={handleSave} disabled={!helpDirty || saving}>
                  Save
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
          {HELP_TOPIC_KEYS.map((key) => (
            <Chip
              key={key}
              label={formatKey(key)}
              onClick={() => setSelectedTopic(key)}
              variant={selectedTopic === key ? "filled" : "outlined"}
              color={selectedTopic === key ? "primary" : "default"}
              size="small"
            />
          ))}
        </Box>

        {selectedTopic && helpTopics[selectedTopic] && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Title"
              value={helpTopics[selectedTopic]?.title || ""}
              onChange={(e) => handleTopicChange(selectedTopic, "title", e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Content"
              value={helpTopics[selectedTopic]?.content || ""}
              onChange={(e) => handleTopicChange(selectedTopic, "content", e.target.value)}
              fullWidth
              multiline
              minRows={6}
              maxRows={20}
              size="small"
            />
          </Box>
        )}
      </Paper>
    </div>
  );
};

export default AdminChatContentEditor;
